<template>
  <div
    class="grow lg:overflow-hidden px-1.5 md:px-2 py-1 lg:py-2 bg-grey-975 flex flex-col overflow-y-auto parapheur-home"
  >
    <div class="grow-0" role="search">
      <div class="flex items-center flex-col md:flex-row">
        <PSearchBar
          data-sentry-id="HomeSearchBarInput"
          size="lg"
          class="w-full lg:w-1/2 [&>input]:bg-white [&>button]:text-[0rem] lg:[&>button]:text-base"
          :class="{
            'max-lg:hidden': panelToShow === 'dashboard',
          }"
          hint="Tapez ici votre recherche..."
          v-model="searchString"
          @search="syncUrl({ searchPattern: searchString.trim() })"
          aria-describedby="searchAdditionalHint"
        />
        <PButton
          data-sentry-id="HomeAdvancedSearchButton"
          mode="tertiary"
          class="hidden lg:flex font-bold my-2 md:my-auto mx-auto md:ml-3 md:max-w-[50%] border-grey-625"
          @click="advancedSearch = !advancedSearch"
          :aria-expanded="advancedSearch"
        >
          Recherche avancée
          <OhVueIcon :name="advancedSearch ? arrowUpIcon : arrowDownIcon" class="ml-1v" />
        </PButton>
        <PButton
          data-sentry-id="HomeResetSearchButton"
          mode="tertiary-borderless"
          class="hidden lg:flex font-medium ml-1"
          @click="resetSearch"
          v-if="panelToShow === 'search'"
          >Retour à l'accueil</PButton
        >
      </div>
      <div
        class="mt-1 text-info"
        :class="{
          'max-lg:hidden': panelToShow === 'dashboard',
        }"
      >
        <OhVueIcon :name="infoIcon" class="mr-1" />
        <span id="searchAdditionalHint">Ajouter * au début pour élargir la recherche.</span>
      </div>
      <div
        :class="{
          'lg:flex': advancedSearch,
          'max-lg:flex': panelToShow === 'search',
        }"
        class="mt-2 hidden flex-wrap gap-1"
      >
        <PSelectableTag
          v-for="tag in allTags"
          :data-sentry-id="`HomeTag-${tag.id}`"
          :key="tag.id"
          :model-value="tags?.includes(tag.name) || false"
          @update:model-value="updateSelectedTags($event, tag.name)"
          class="sm:mx-1v"
        >
          {{ tag.name }}
        </PSelectableTag>
        <!-- <PSelectableTag
          :model-value="alreadySeen || false"
          @update:model-value="syncUrl({ alreadySeen: $event })"
          class="mx-1"
        >
          Déjà vu
        </PSelectableTag> -->
        <PSelectableTag
          data-sentry-id="HomeTag-CreatedByMe"
          :model-value="createdByMe || false"
          @update:model-value="syncUrl({ createdByMe: $event })"
          class="sm:mx-1v"
        >
          Initié par moi
        </PSelectableTag>
        <div
          class="hidden lg:inline border-l border-grey-100 mx-2 basis-full border-t sm:basis-auto sm:border-t-0"
        ></div>
        <PSelectableTag
          data-sentry-id="HomeTag-Upstream"
          :model-value="inTodo || false"
          @update:model-value="syncUrl({ inTodo: $event })"
          class="sm:mx-1v"
          :disabled="inCurrent && inDone && inCC"
        >
          Amont
        </PSelectableTag>
        <PSelectableTag
          data-sentry-id="HomeTag-Current"
          :model-value="inCurrent || false"
          @update:model-value="syncUrl({ inCurrent: $event })"
          class="sm:mx-1v"
          :disabled="inDone && inTodo && inCC"
        >
          À mon niveau
        </PSelectableTag>
        <PSelectableTag
          data-sentry-id="HomeTag-Downstream"
          :model-value="inDone || false"
          @update:model-value="syncUrl({ inDone: $event })"
          class="sm:mx-1v"
          :disabled="inCurrent && inTodo && inCC"
        >
          Aval
        </PSelectableTag>
        <PSelectableTag
          data-sentry-id="HomeTag-CC"
          :model-value="inCC || false"
          @update:model-value="syncUrl({ inCC: $event })"
          class="sm:mx-1v"
          :disabled="inCurrent && inDone && inTodo"
        >
          En copie
        </PSelectableTag>
        <div
          class="hidden lg:inline border-l border-grey-100 mx-2 basis-full border-t sm:basis-auto sm:border-t-0"
        ></div>
        <PSelectableTag
          data-sentry-id="HomeTag-Archived"
          :model-value="archived === true"
          @update:model-value="
            syncUrl({
              archived: $event ? true : null,
            })
          "
          class="sm:mx-1v"
        >
          Archivé
        </PSelectableTag>
        <PSelectableTag
          data-sentry-id="HomeTag-NotArchived"
          :model-value="archived === false"
          @update:model-value="
            syncUrl({
              archived: $event ? false : null,
            })
          "
          class="sm:mx-1v"
        >
          Non Archivé
        </PSelectableTag>
      </div>
      <div v-show="advancedSearch" class="mt-2 flex items-center flex-col lg:flex-row lg:gap-12 px-2 lg:px-0">
        <fieldset class="flex flex-col w-full lg:w-auto">
          <legend class="text-sm text-blue-france-sun-113">Parapheur actif</legend>
          <div class="flex items-center gap-2">
            <span class="mb-3 text-sm">Du</span>
            <PDateTimePicker
              data-sentry-id="HomeActiveSinceInput"
              title="Parapheur actif depuis"
              label=""
              :model-value="startDateNormalized"
              @update:model-value="syncUrl({ startDate: $event as string })"
              :disabled-dates="getDisallowedStartDates"
              aria-label="Date de début: format jj/mm/aaaa"
            />
            <span class="mb-3 text-sm">au</span>
            <PDateTimePicker
              data-sentry-id="HomeActiveUntilInput"
              title="Parapheur jusqu'au"
              label=""
              :model-value="endDateNormalized"
              @update:model-value="syncUrl({ endDate: $event as string })"
              :disabled-dates="getDisallowedEndDates"
              aria-label="Date de fin: format jj/mm/aaaa"
            />
          </div>
        </fieldset>
        <fieldset class="flex flex-col w-full lg:w-auto">
          <legend class="text-sm text-blue-france-sun-113">Parapheur initié</legend>
          <div class="flex items-center gap-2">
            <span class="mb-3 text-sm">Du</span>
            <PDateTimePicker
              data-sentry-id="HomeInitiatedSinceInput"
              title="Parapheur initié du"
              label=""
              :model-value="createdStartDateNormalized"
              @update:model-value="syncUrl({ createdStartDate: $event as string })"
              :disabled-dates="getDisallowedCreatedStartDates"
              aria-label="Date de début: format jj/mm/aaaa"
            />
            <span class="mb-3 text-sm">au</span>
            <PDateTimePicker
              data-sentry-id="HomeInitiatedUntilInput"
              title="Parapheur initié jusqu'au"
              label=""
              :model-value="createdEndDateNormalized"
              @update:model-value="syncUrl({ createdEndDate: $event as string })"
              :disabled-dates="getDisallowedCreatedEndDates"
              aria-label="Date de fin: format jj/mm/aaaa"
            />
          </div>
        </fieldset>
        <div class="flex flex-col w-full lg:w-1/4 xl:w-1/5">
          <span class="text-sm text-blue-france-sun-113">Type de fichier</span>
          <PSelect
            data-sentry-id="HomeFileTypeSelect"
            label=""
            :model-value="fileTypes || []"
            @update:model-value="syncUrl({ fileTypes: $event })"
            :options="fileTypeOptions"
            class="pt-1v"
          />
        </div>
      </div>
    </div>
    <div v-show="panelToShow === 'dashboard'" class="mb-7 mt-1 lg:mt-2 lg:mb-4 flex justify-end gap-1">
      <MassSignModal v-model="isMassSignOpen" v-if="isMassSignEnabled" />
      <ChooseReaderModal />
      <PButton
        data-sentry-id="HomeCreateLetterFileButton"
        to="letter-file"
        :mode="massSignatureStore.selectedLetterFiles.length ? 'secondary' : 'primary'"
      >
        <OhVueIcon :name="activatorIcon" class="mr-1" />
        Créer un parapheur
      </PButton>
    </div>
    <div class="grow-0 overflow-y-scroll" v-show="panelToShow === 'search'">
      <div class="flex items-center">
        <h1
          class="text-h6 lg:text-h4 font-bold mb-1 lg:mb-4"
          :class="{ 'mt-2': !advancedSearch }"
          aria-live="polite"
          :aria-atomic="true"
        >
          <span v-if="lastSubmittedSearchPattern">
            {{ letterFileStore.list.total }} résultats pour "{{ lastSubmittedSearchPattern }}"
          </span>
          <span v-else> {{ letterFileStore.list.total }} résultats </span>
        </h1>
        <div class="hidden lg:block mb-1.5 ml-auto">
          <ExportLinkLoader :search-body="searchBody" :disabled="!canStartExport" />
        </div>
      </div>
      <div
        v-if="forceSearchDisplay"
        class="lg:overflow-hidden lg:overflow-y-scroll pr-1 lg:pr-2 lg:pb-1"
        ref="searchResultsEl"
      >
        <ul class="flex flex-col gap-2">
          <li v-for="(result, index) in searchResults" :key="result.id">
            <SearchResult :letter-file="result" :class="{ 'first-search-result': index === 0 }" />
          </li>
        </ul>
        <PPagination
          v-show="searchResults.length"
          :total="letterFileStore.list.total"
          :current-page="letterFileStore.list.page"
          :page-size="letterFileStore.list.limit"
          @change="syncUrl"
        />
      </div>
    </div>
    <Dashboard class="flex grow lg:h-0" v-show="panelToShow === 'dashboard'" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
  PSearchBar,
  PSelectableTag,
  useIcons,
  PButton,
  PDateTimePicker,
  PSelect,
  PPagination,
  usePastaRouter,
} from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import { RiAddLine, RiArrowDropDownLine, RiArrowDropUpLine, BiInfoSquareFill } from "oh-vue-icons/icons";
import Dashboard from "./views/dashboard/Dashboard.vue";
import SearchResult from "./components/SearchResult.vue";
import ExportLinkLoader from "./components/ExportLinkLoader.vue";
import { type LetterFileSearchDto } from "./dto/letter-file.dto";
import { isArray, isNil, omit, pickBy, uniq } from "lodash";
import { useLetterFileStore } from "./stores/letter-file.store";
import dayjs from "dayjs";
import Mark from "mark.js";
import { useExportStore } from "./stores/export.store";
import { useHead } from "@unhead/vue";
import MassSignModal from "./components/MassSignModal.vue";
import { useUserPreferencesStore } from "./stores/user-preferences.store";
import ChooseReaderModal from "./components/ChooseReaderModal.vue";
import { useMassSign } from "./composable/use-mass-sign";

interface Props {
  page: number;
  pageSize: number;
  searchPattern?: string;
  archived?: boolean | null;
  tags?: string[];
  alreadySeen?: boolean;
  createdByMe?: boolean;
  inTodo?: boolean;
  inCurrent?: boolean;
  inDone?: boolean;
  inCC: boolean;
  startDate?: string;
  endDate?: string;
  createdStartDate?: string;
  createdEndDate?: string;
  fileTypes?: string[];
}
const props = withDefaults(defineProps<Props>(), {
  page: 1,
  pageSize: 25,
});

const startDateNormalized = computed(() => {
  if (!props.startDate) return "";
  return normalizeDateAsStartOfDay(props.startDate);
});
const endDateNormalized = computed(() => {
  if (!props.endDate) return "";
  return normalizeDateAsEndOfDay(props.endDate);
});
const createdStartDateNormalized = computed(() => {
  if (!props.createdStartDate) return "";
  return normalizeDateAsStartOfDay(props.createdStartDate);
});
const createdEndDateNormalized = computed(() => {
  if (!props.createdEndDate) return "";
  return normalizeDateAsEndOfDay(props.createdEndDate);
});

const head = useHead({})!;

const [activatorIcon, arrowDownIcon, arrowUpIcon, infoIcon] = useIcons(
  RiAddLine,
  RiArrowDropDownLine,
  RiArrowDropUpLine,
  BiInfoSquareFill
);
const letterFileStore = useLetterFileStore();
const userPreferencesStore = useUserPreferencesStore();
const $router = usePastaRouter();
const allTags = computed(() => userPreferencesStore.tags || []);

const searchString = ref<string>("");
const lastSubmittedSearchPattern = ref<string>("");
const fileTypeOptions = [
  { label: "Tous", value: [] },
  { label: "Pdf", value: [".pdf"] },
  {
    label: "Diaporama",
    value: [".odp", ".otp", ".pps", ".ppsm", ".ppsx", ".ppt", ".pptm", ".pptx"],
  },
  {
    label: "Texte et mel",
    value: [".doc", ".docx", ".docm", ".dotx", ".dotm", ".eml", ".msg", ".odt", ".ott", ".rtf"],
  },
  {
    label: "Tableur",
    value: [".csv", ".ots", ".ods", ".txt", ".xls", ".xlsm", ".xlsx", ".xltx", ".xltm"],
  },
  {
    label: "Média",
    value: [
      ".avi",
      ".gif",
      ".jpe",
      ".jpeg",
      ".jpg",
      ".mov",
      ".png",
      ".tif",
      ".tiff",
      ".mp3",
      ".bmp",
      ".svg",
      ".dxf",
      ".mp4",
      ".mkv",
      ".vmv",
      ".flv",
    ],
  },
  {
    label: "Internet",
    value: [".xml", ".css", ".htm", ".html", ".mht"],
  },
  {
    label: "Fichier compressé",
    value: [".rar", ".tar", ".tgz", ".zip", ".zed", ".7z"],
  },
];

const advancedSearch = ref<boolean>(false);
const panelToShow = ref<"search" | "dashboard">("dashboard");
const searchResults = computed(() => letterFileStore.list.data);

async function syncUrl(
  $event: LetterFileSearchDto & {
    page?: number;
    pageSize?: number;
  }
) {
  searchString.value = searchString.value.trim();

  const query: Partial<Record<keyof LetterFileSearchDto | "page" | "pageSize", string | string[]>> = {
    page: props.page.toString(),
    pageSize: props.pageSize.toString(),
  };
  if (props.searchPattern) query.searchPattern = props.searchPattern;
  if (props.alreadySeen || $event.alreadySeen) query.alreadySeen = "true";
  if (!isNil(props.archived)) query.archived = `${props.archived}`;
  if (!isNil($event.archived)) query.archived = `${$event.archived}`;
  if ($event.archived === null) delete query.archived;
  if (props.createdByMe || $event.createdByMe) query.createdByMe = "true";
  if (props.inTodo || $event.inTodo) query.inTodo = "true";
  if (props.inCurrent || $event.inCurrent) query.inCurrent = "true";
  if (props.inDone || $event.inDone) query.inDone = "true";
  if (props.inCC || $event.inCC) query.inCC = "true";

  if (startDateNormalized.value) {
    query.startDate = startDateNormalized.value;
  }
  if (props.endDate) query.endDate = endDateNormalized.value;
  if (createdStartDateNormalized.value) {
    query.createdStartDate = createdStartDateNormalized.value;
  }
  if (props.createdEndDate) query.createdEndDate = createdEndDateNormalized.value;
  if (props.fileTypes?.length) query.fileTypes = props.fileTypes;
  if (props.tags?.length) query.tags = props.tags;

  const searchParams = searchParamsToString($event);
  await $router.replace({
    query: {
      ...query,
      ...searchParams,
    },
  });
}

function searchParamsToString(
  obj: LetterFileSearchDto & {
    page?: number;
    pageSize?: number;
  }
) {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => !isNil(v))
      .map(([k, v]) => {
        switch (k) {
          case "tags":
          case "fileTypes":
            return [k, v];
          case "startDate":
          case "createdStartDate":
            return [k, normalizeDateAsStartOfDay(v)];
          case "endDate":
          case "createdEndDate":
            return [k, normalizeDateAsEndOfDay(v)];
          default:
            return [k, `${v}`];
        }
      })
  );
}

function updateSelectedTags(add: boolean, name: string) {
  if (add) {
    syncUrl({
      tags: uniq([...(props.tags || []), name]),
    });
  } else {
    syncUrl({
      tags: (props.tags || []).filter(tag => tag !== name),
    });
  }
}
function resetSearchLite() {
  lastSubmittedSearchPattern.value = "";
  searchString.value = "";
  panelToShow.value = "dashboard";
  head.patch({ title: "Accueil - Le parapheur" });
  letterFileStore.setList({
    total: 0,
    data: [],
    limit: 0,
    page: 1,
    offset: 0,
    isLastPage: true,
  });
}
async function resetSearch() {
  resetSearchLite();
  await $router.replace({
    query: {},
  });
}

const searchBody = computed(() => {
  return pickBy(
    omit(props, "page", "pageSize"),
    (value, key) => key === "archived" || (isArray(value) ? value.length : !!value)
  );
});

async function search() {
  await nextTick();
  await letterFileStore.search(searchBody.value, {
    page: props.page,
    pageSize: props.pageSize,
  });
  lastSubmittedSearchPattern.value = props.searchPattern || "";
  panelToShow.value = "search";
  head.patch({ title: "Recherche - Le parapheur" });
  await updateMark();
  (document.querySelector(".first-search-result") as HTMLElement)?.focus();
}

watch(
  () => $router.vueRouter.currentRoute.value.query,
  async value => {
    if (Object.keys(value).length) {
      await search();
    } else {
      resetSearchLite();
    }
  }
);

onMounted(async () => {
  head.patch({
    title: () => (panelToShow.value === "dashboard" ? "Accueil - Le parapheur" : "Recherche - Le parapheur"),
  });
  const query = $router.vueRouter.currentRoute.value.query;
  if (Object.keys(query).length) {
    searchString.value = props.searchPattern || "";
    if (Object.keys(omit(query, "searchPattern", "endDate", "createdEndDate", "page", "pageSize")).length) {
      advancedSearch.value = true;
    }
    await search();
  }
});

function getDisallowedStartDates(val: Date) {
  if (!endDateNormalized.value) return false;
  return dayjs(val)
    .add(val.getTimezoneOffset() * -1, "minute")
    .isAfter(endDateNormalized.value);
}

function getDisallowedEndDates(val: Date) {
  if (!startDateNormalized.value) return false;
  return dayjs(val)
    .add(val.getTimezoneOffset() * -1, "minute")
    .isBefore(startDateNormalized.value);
}

function getDisallowedCreatedStartDates(val: Date) {
  if (!createdEndDateNormalized.value) return false;
  return dayjs(val)
    .add(val.getTimezoneOffset() * -1, "minute")
    .isAfter(createdEndDateNormalized.value);
}

function getDisallowedCreatedEndDates(val: Date) {
  if (!createdStartDateNormalized.value) return false;
  return dayjs(val)
    .add(val.getTimezoneOffset() * -1, "minute")
    .isBefore(createdStartDateNormalized.value);
}

// Highlight
const forceSearchDisplay = ref<boolean>(true);
const searchResultsEl = ref<HTMLElement | null>(null);
const mark = ref<Mark | null>(null);
async function updateMark() {
  forceSearchDisplay.value = false;
  await nextTick();
  forceSearchDisplay.value = true;
  await nextTick();
  if (mark.value) {
    mark.value.unmark();
  }
  mark.value = new Mark(searchResultsEl.value!);
  mark.value.markRegExp(/\$\{.*?\}\$/, {
    element: "span",
    className: "highlighted-result bg-[#FBF0BB]",
    acrossElements: true,
    each: (el: HTMLElement) => {
      el.textContent = el.textContent!.replaceAll("${", "").replaceAll("}$", "");
    },
  });
}

// export
const exportStore = useExportStore();
const canStartExport = computed(() => exportStore.canStartExport);
async function fetchCanStartExport() {
  await exportStore.fetchCanStartExport();
}
onMounted(fetchCanStartExport);
watch(
  () => advancedSearch.value,
  async val => {
    if (val) await exportStore.fetchCanStartExport();
  }
);

const { isMassSignOpen, isMassSignEnabled, massSignatureStore } = useMassSign();

function normalizeDateAsStartOfDay(value: string) {
  if (value === "") {
    return value;
  }
  return dayjs(value).utcOffset(0).hour(0).minute(0).second(0).millisecond(0).toISOString();
}

function normalizeDateAsEndOfDay(value: string) {
  if (value === "") {
    return value;
  }
  return dayjs(value).utcOffset(0).hour(23).minute(59).second(59).millisecond(999).toISOString();
}
</script>
